import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  GUIDANCE_COPY,
  isContextRejection,
  readGuidanceResponse,
} from "../../lib/keyshawn/keyshawnGuidanceContext";
import axiosInstance from "../../utils/axiosInstance";
import { updateTaskStatus } from "../tasks/slice";

// Completion states matching the backend state machine
export const COMPLETION_STATES = {
  OPEN: "OPEN",
  PRESSURE_TEST_PENDING: "PRESSURE_TEST_PENDING",
  PRESSURE_TEST_COMPLETE: "PRESSURE_TEST_COMPLETE",
  CLOSED: "CLOSED",
  CLOSED_WITHOUT_TEST: "CLOSED_WITHOUT_TEST",
};

const TASK_STATUS_SYNC_ERROR = {
  code: "TASK_STATUS_SYNC_FAILED",
  message: "Failed to synchronize completed task status",
};
const TASK_STATUS_SYNC_TIMEOUT_MS = 10_000;

// Default state for a single task's coaching session
const defaultTaskState = {
  messages: [],
  completionState: COMPLETION_STATES.OPEN,
  taskStatusSyncError: null,
  suggestedQuestions: [],
  isLoading: false,
};

// Helper: get the active task's coaching state safely
const getTaskState = (state) =>
  state.taskStates[state.activeTaskId] || defaultTaskState;

// Helper: ensure the active task has an entry in taskStates, then return it
const ensureTaskState = (state) => {
  if (!state.activeTaskId) return null;
  if (!state.taskStates[state.activeTaskId]) {
    state.taskStates[state.activeTaskId] = {
      messages: [],
      completionState: COMPLETION_STATES.OPEN,
      taskStatusSyncError: null,
      suggestedQuestions: [],
      isLoading: false,
    };
  }
  return state.taskStates[state.activeTaskId];
};

// Helper: ensure a specific task (by ID from thunk arg) has an entry, then return it.
// Used by extraReducers to route results to the originating task, not the currently
// active task (which may have changed if the student switched tasks mid-flight).
const ensureTaskStateById = (state, taskId) => {
  if (!taskId) return null;
  if (!state.taskStates[taskId]) {
    state.taskStates[taskId] = {
      messages: [],
      completionState: COMPLETION_STATES.OPEN,
      taskStatusSyncError: null,
      suggestedQuestions: [],
      isLoading: false,
    };
  }
  return state.taskStates[taskId];
};

// Fetch level resources for the coaching side panel
export const fetchLevelResources = createAsyncThunk(
  "coaching/fetchLevelResources",
  async ({ challengeId, levelNumber }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(
        `/resources/${challengeId}/${levelNumber}`,
        "get",
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch resources",
      );
    }
  },
);

// Fetch suggested questions on task open
export const fetchSuggestedQuestions = createAsyncThunk(
  "coaching/fetchSuggestedQuestions",
  async ({ taskId } = {}, { rejectWithValue }) => {
    try {
      const response = await axiosInstance("/coaching/suggest", "post", {
        task_id: taskId || undefined,
      });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch suggestions",
      );
    }
  },
);

// Send a chat message to Keyshawn.
// stepType/stepEntityId are the explicit execution marker (Chunk C-µ):
// WorkStep's ChatView passes stepType="execution" + the task id; SquadWorkspace
// passes nothing, so both keys stay undefined and never serialize into the
// JSON body (same pattern as useKeyshawnChat) - legacy requests are unchanged.
export const sendChat = createAsyncThunk(
  "coaching/sendChat",
  async ({ message, taskId, stepType, stepEntityId }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance("/coaching/chat", "post", {
        message,
        task_id: taskId || undefined,
        step_type: stepType || undefined,
        step_entity_id: stepEntityId != null ? stepEntityId : undefined,
      });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send message",
      );
    }
  },
);

// FE-G1 guidance v2: Work's scoped chat transport. The payload is the
// whitelisted builder's (keyshawnGuidanceContext.buildGuidancePayload), and
// every settle is keyed on identity key + generation + request id, applied
// ONLY to `state.guidance`, never to a task's thread or completion state.
// The caller holds the returned promise and aborts it on unmount or context
// change; the signal reaches axios through its fourth argument.
export const sendChatV2 = createAsyncThunk(
  "coaching/sendChatV2",
  // `key`, `generation`, `requestId` and `studentText` are read by the
  // reducers from `meta.arg`; only the payload reaches the network.
  async ({ payload }, { rejectWithValue, signal }) => {
    try {
      const response = await axiosInstance("/coaching/chat", "post", payload, {
        signal,
      });
      return readGuidanceResponse(response?.data?.data ?? response?.data);
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status ?? null,
      });
    }
  },
);

// Trigger pressure test on mark complete
export const triggerPressureTest = createAsyncThunk(
  "coaching/triggerPressureTest",
  async ({ taskId } = {}, { rejectWithValue, dispatch, signal }) => {
    try {
      const response = await axiosInstance("/coaching/pressure-test", "post", {
        task_id: taskId || undefined,
      });
      const data = response.data.data || response.data;

      if (data.task_completed && data.task_id) {
        const syncPromise = dispatch(
          updateTaskStatus({ id: data.task_id, status_id: 5 }),
        );
        const abortSync = () => syncPromise.abort();
        if (signal.aborted) {
          abortSync();
        } else {
          signal.addEventListener("abort", abortSync, { once: true });
        }
        const timeoutId = setTimeout(abortSync, TASK_STATUS_SYNC_TIMEOUT_MS);

        try {
          await syncPromise.unwrap();
        } catch {
          return {
            ...data,
            task_status_sync_error: { ...TASK_STATUS_SYNC_ERROR },
          };
        } finally {
          clearTimeout(timeoutId);
          signal.removeEventListener("abort", abortSync);
        }
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Pressure test unavailable",
      );
    }
  },
);

// Confirm task completion after pressure test
// Bug 2 fix: dispatch updateTaskStatus using the backend's authoritative task_id
export const confirmComplete = createAsyncThunk(
  "coaching/confirmComplete",
  async ({ taskId } = {}, { rejectWithValue, dispatch }) => {
    try {
      const response = await axiosInstance(
        "/coaching/confirm-complete",
        "post",
        { task_id: taskId || undefined },
      );
      const data = response.data.data || response.data;

      // Sync task status using backend's authoritative task_id
      if (data.task_completed && data.task_id) {
        dispatch(updateTaskStatus({ id: data.task_id, status_id: 5 }));
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to confirm completion",
      );
    }
  },
);

// Log resource interaction.
// Body must match ResourceIntelligenceController::logInteraction exactly:
//   required: challenge_id, level_number, step_number, interaction_type
//            (interaction_type in: opened|scrolled|searched|bookmarked)
//   optional: duration_seconds, pages_viewed, search_query
// resource_id is the path param, not a body field; everything else on the
// interactionData object passes straight through (so callers can add the
// optional fields when available).
//
// Capture Gap Closure: the deployed backend (PR #147) accepts one more
// interaction_type than the required-list above enumerates, and
// tests/Fixtures/capture_gap_contract_v1.json is the authority for the full
// set. This frontend sends `opened` and `closed` only. duration_seconds and
// pages_viewed are integer, minimum 0, and nullable - a reader with no page
// concept sends pages_viewed: null rather than a manufactured count.
export const logResourceInteraction = createAsyncThunk(
  "coaching/logResourceInteraction",
  async (interactionData, { rejectWithValue }) => {
    try {
      const { resource_id, ...body } = interactionData;
      const response = await axiosInstance(
        `/resources/${resource_id}/interactions`,
        "post",
        body,
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to log interaction",
      );
    }
  },
);

const initialState = {
  // Per-task coaching state includes completion and task-status sync results.
  taskStates: {},
  // FE4 (second first-exposure run): task ids the SERVER reported complete
  // through Keyshawn (`task_completed === true` from the pressure test or
  // the confirm). The workspace reconciles its own task state from these,
  // without a write of its own.
  serverCompleted: {},
  // Currently active task ID
  activeTaskId: null,
  // Level resources (shared across tasks in the same level)
  resources: [],
  isSuggestionsLoading: false,
  isResourcesLoading: false,
  error: null,
  // FE-G1 guidance v2 (Work only). `key` stays null unless the in-drawer
  // chat runs the v2 path, which needs the guidance flag on and an eligible
  // student. A separate transcript: task threads, suggestions and completion
  // state are never touched by it.
  guidance: {
    key: null,
    generation: 0,
    pendingRequestId: null,
    messages: [],
  },
};

/** The Work guidance record, tolerating a hand-built store without one. */
const workGuidanceOf = (state) => {
  if (!state.guidance) {
    state.guidance = {
      key: null,
      generation: 0,
      pendingRequestId: null,
      messages: [],
    };
  }
  return state.guidance;
};

/** A v2 action belongs to the live Work identity only on an exact match. */
const isLiveWorkIdentity = (guidance, arg) =>
  guidance.key !== null &&
  arg?.key === guidance.key &&
  arg?.generation === guidance.generation;

const guidanceReply = (text, meta) => ({
  id: `keyshawn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sender: "keyshawn",
  text,
  timestamp: new Date().toISOString(),
  guidance: meta,
});

const coachingSlice = createSlice({
  name: "coaching",
  initialState,
  reducers: {
    // Set active task: initializes task state if it doesn't exist
    setActiveTask: (state, action) => {
      const taskId = action.payload;
      state.activeTaskId = taskId;
      if (taskId && !state.taskStates[taskId]) {
        state.taskStates[taskId] = {
          messages: [],
          completionState: COMPLETION_STATES.OPEN,
          taskStatusSyncError: null,
          suggestedQuestions: [],
          isLoading: false,
        };
      }
    },
    // Full reset: clears all task states (used when closing workspace entirely)
    resetCoaching: () => initialState,
    // Reset only completion state for the active task
    resetCompletionState: (state) => {
      const ts = ensureTaskState(state);
      if (ts) {
        ts.completionState = COMPLETION_STATES.OPEN;
      }
    },
    // Add a student message to the active task's thread immediately (optimistic)
    addStudentMessage: (state, action) => {
      const ts = ensureTaskState(state);
      if (ts) {
        ts.messages.push({
          id: `student-${Date.now()}`,
          sender: "student",
          text: action.payload,
          timestamp: new Date().toISOString(),
        });
      }
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // FE-G1: the Work chat's identity. A new key starts a new generation and
    // drops the old transcript and any pending request; `null` leaves v2.
    setWorkGuidanceKey: (state, action) => {
      const guidance = workGuidanceOf(state);
      const key =
        typeof action.payload?.key === "string" ? action.payload.key : null;
      if (key === guidance.key) return;
      guidance.key = key;
      guidance.generation += 1;
      guidance.pendingRequestId = null;
      guidance.messages = [];
    },
  },
  extraReducers: (builder) => {
    // fetchLevelResources
    builder
      .addCase(fetchLevelResources.pending, (state) => {
        state.isResourcesLoading = true;
      })
      .addCase(fetchLevelResources.fulfilled, (state, action) => {
        state.isResourcesLoading = false;
        state.resources = action.payload;
      })
      .addCase(fetchLevelResources.rejected, (state, action) => {
        state.isResourcesLoading = false;
        state.error = action.payload;
      });

    // fetchSuggestedQuestions - store in originating task's state
    builder
      .addCase(fetchSuggestedQuestions.pending, (state) => {
        state.isSuggestionsLoading = true;
      })
      .addCase(fetchSuggestedQuestions.fulfilled, (state, action) => {
        state.isSuggestionsLoading = false;
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.suggestedQuestions = action.payload?.questions || [];
        }
      })
      .addCase(fetchSuggestedQuestions.rejected, (state, action) => {
        state.isSuggestionsLoading = false;
        // Suggestions failing is non-critical, don't set error
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.suggestedQuestions = [];
        }
      });

    // sendChat - push messages to originating task's thread
    builder
      .addCase(sendChat.pending, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) ts.isLoading = true;
        state.error = null;
      })
      .addCase(sendChat.fulfilled, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = false;
          ts.messages.push({
            id: `keyshawn-${Date.now()}`,
            sender: "keyshawn",
            text: action.payload?.response || action.payload?.message || "",
            refSection: action.payload?.ref_section || null,
            refSections:
              action.payload?.ref_sections ||
              (action.payload?.ref_section ? [action.payload.ref_section] : []),
            timestamp: new Date().toISOString(),
          });
        }
      })
      .addCase(sendChat.rejected, (state, action) => {
        state.error = action.payload;
        // Add fallback message so student isn't left hanging
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = false;
          ts.messages.push({
            id: `keyshawn-error-${Date.now()}`,
            sender: "keyshawn",
            text: "i'm having trouble connecting right now. try again in a moment.",
            timestamp: new Date().toISOString(),
          });
        }
      });

    // FE-G1 sendChatV2: only `state.guidance`, only the live identity, and
    // a settle only for the request still pending. No pending record means
    // drop, so a late reply can never create state.
    builder
      .addCase(sendChatV2.pending, (state, action) => {
        const guidance = workGuidanceOf(state);
        const arg = action.meta.arg;
        if (!isLiveWorkIdentity(guidance, arg) || !arg?.requestId) return;
        guidance.messages.push({
          id: `student-${arg.requestId}`,
          sender: "student",
          text: arg.studentText,
          timestamp: new Date().toISOString(),
        });
        guidance.pendingRequestId = arg.requestId;
      })
      .addCase(sendChatV2.fulfilled, (state, action) => {
        const guidance = workGuidanceOf(state);
        const arg = action.meta.arg;
        if (!isLiveWorkIdentity(guidance, arg)) return;
        if (!arg?.requestId || guidance.pendingRequestId !== arg.requestId) {
          return;
        }
        guidance.pendingRequestId = null;
        const reply = action.payload;
        // S5b: a Squad document question names its file; a reply whose echo
        // names another file (or none) is never stored.
        const squadMismatch =
          arg.expectFileId != null &&
          reply?.squadDocument?.fileId !== arg.expectFileId;
        if (!reply?.text || squadMismatch) {
          guidance.messages.push(
            guidanceReply(GUIDANCE_COPY.transportFailed, {
              contextStatus: "unavailable",
              contextLabel: null,
              sources: [],
              actions: [],
              fallback: false,
              kind: "failed",
              retryText: arg.studentText,
            }),
          );
          return;
        }
        guidance.messages.push(
          guidanceReply(reply.text, {
            contextStatus: reply.contextStatus,
            contextLabel: reply.contextLabel,
            sources: reply.sources,
            actions: reply.actions,
            fallback: reply.fallback,
            kind: "reply",
            requestedPair: arg.requestedPair ?? null,
            ...(arg.expectFileId != null
              ? { squadDocument: reply.squadDocument }
              : {}),
          }),
        );
      })
      .addCase(sendChatV2.rejected, (state, action) => {
        const guidance = workGuidanceOf(state);
        const arg = action.meta.arg;
        if (!isLiveWorkIdentity(guidance, arg)) return;
        if (!arg?.requestId || guidance.pendingRequestId !== arg.requestId) {
          return;
        }
        guidance.pendingRequestId = null;
        if (action.meta.aborted) return;
        if (isContextRejection(action.payload?.status)) {
          guidance.messages.push(
            guidanceReply(GUIDANCE_COPY.contextRejected, {
              contextStatus: "unavailable",
              contextLabel: null,
              sources: [],
              actions: [],
              fallback: false,
              kind: "rejected",
            }),
          );
          return;
        }
        guidance.messages.push(
          guidanceReply(GUIDANCE_COPY.transportFailed, {
            contextStatus: "unavailable",
            contextLabel: null,
            sources: [],
            actions: [],
            fallback: false,
            kind: "failed",
            retryText: arg.studentText,
          }),
        );
      });

    // triggerPressureTest - update originating task's completion state
    builder
      .addCase(triggerPressureTest.pending, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = true;
          ts.completionState = COMPLETION_STATES.PRESSURE_TEST_PENDING;
          ts.taskStatusSyncError = null;
          ts.checkFailed = false;
        }
      })
      .addCase(triggerPressureTest.fulfilled, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = false;
          ts.completionState =
            action.payload?.state || COMPLETION_STATES.PRESSURE_TEST_COMPLETE;
          if (action.payload?.task_completed === true) {
            const doneId = action.payload?.task_id ?? taskId;
            if (doneId) state.serverCompleted[doneId] = true;
          }
          ts.taskStatusSyncError =
            action.payload?.task_status_sync_error || null;
          ts.messages.push({
            id: `pressure-test-${Date.now()}`,
            sender: "keyshawn",
            text: action.payload?.response || action.payload?.message || "",
            isPressureTest: true,
            timestamp: new Date().toISOString(),
          });
        }
      })
      .addCase(triggerPressureTest.rejected, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = false;
          // FE4 round 5 (truthfulness, authorized by Jenni as the same class
          // as the confirm fix): the REQUEST failed, so nothing was written
          // and nothing is known. The task stays open, coaching stays open,
          // the check stays available to retry, and the surface says only
          // that the check did not happen. It previously claimed completion
          // and locked coaching with no completion write.
          ts.completionState = COMPLETION_STATES.OPEN;
          ts.checkFailed = true;
        }
      });

    // logResourceInteraction - fire-and-forget instrumentation.
    // Surface failures (e.g. a contract/validation mismatch) instead of
    // swallowing them silently, but do NOT set state.error: this is
    // background telemetry and must not trip the resource-load or
    // confirm-complete error UI, which both read state.error.
    builder.addCase(logResourceInteraction.rejected, (_state, action) => {
      if (typeof console !== "undefined" && console.warn) {
        console.warn(
          "logResourceInteraction failed:",
          action.payload || action.error?.message,
          // include the originating args so a failed log identifies which
          // resource / challenge / level / step it was trying to record
          action.meta?.arg,
        );
      }
    });

    // confirmComplete - update originating task's completion state
    builder
      .addCase(confirmComplete.pending, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = true;
          ts.confirmRefused = false;
        }
      })
      .addCase(confirmComplete.fulfilled, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) {
          ts.isLoading = false;
          // FE4 checkpoint (b): the backend answers 200 with
          // `task_completed: false` when it refuses (no pressure test on
          // record, still pending, no task). Only a confirmed completion
          // closes; a refusal keeps the confirmation available and says so.
          if (action.payload?.task_completed === true) {
            ts.completionState = COMPLETION_STATES.CLOSED;
            ts.confirmRefused = false;
            const doneId = action.payload?.task_id ?? taskId;
            if (doneId) state.serverCompleted[doneId] = true;
          } else {
            ts.confirmRefused = true;
          }
        }
      })
      .addCase(confirmComplete.rejected, (state, action) => {
        const taskId = action.meta.arg?.taskId || state.activeTaskId;
        const ts = ensureTaskStateById(state, taskId);
        if (ts) ts.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setActiveTask,
  resetCoaching,
  resetCompletionState,
  addStudentMessage,
  clearError,
  setWorkGuidanceKey,
} = coachingSlice.actions;

// FE-G1: the Work v2 transcript. `?.` for hand-built stores.
const EMPTY_WORK_GUIDANCE = {
  key: null,
  generation: 0,
  pendingRequestId: null,
  messages: [],
};
export const selectWorkGuidance = (state) =>
  state.coaching?.guidance ?? EMPTY_WORK_GUIDANCE;

// Selectors: read from the active task's state
export const selectMessages = (state) => getTaskState(state.coaching).messages;
/** FE4: the last confirmation was answered but not completed. */
const EMPTY_COMPLETED = {};
export const selectServerCompleted = (state) =>
  state.coaching?.serverCompleted ?? EMPTY_COMPLETED;

export const selectCheckFailed = (state) =>
  getTaskState(state.coaching).checkFailed === true;

export const selectConfirmRefused = (state) =>
  getTaskState(state.coaching).confirmRefused === true;
export const selectCompletionState = (state) =>
  getTaskState(state.coaching).completionState;
export const selectSuggestedQuestions = (state) =>
  getTaskState(state.coaching).suggestedQuestions;
export const selectIsLoading = (state) =>
  getTaskState(state.coaching).isLoading;

export default coachingSlice.reducer;
